import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  Copy,
  Database,
  Play,
  Plus,
  RefreshCcw,
  Sparkles,
} from "lucide-react";

const Page = styled.main`
  min-height: 100vh;
  padding: 110px 20px 90px;
  color: #e5e7eb;
  background:
    radial-gradient(880px at 12% 10%, rgba(245, 158, 11, 0.14), transparent 58%),
    radial-gradient(900px at 88% 12%, rgba(56, 189, 248, 0.14), transparent 60%),
    linear-gradient(180deg, #020617 0%, #04111f 100%);
`;

const Shell = styled.div`
  max-width: 1380px;
  margin: 0 auto;
`;

const Hero = styled.section`
  padding: 30px;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(2, 6, 23, 0.84)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.12), transparent 32%);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.34);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  color: #f8fafc;
  font-size: clamp(36px, 5vw, 56px);
  line-height: 1.04;
`;

const Sub = styled.p`
  margin: 16px 0 0;
  max-width: 860px;
  color: #94a3b8;
  font-size: 16px;
  line-height: 1.8;
`;

const MainGrid = styled.section`
  margin-top: 22px;
  display: grid;
  grid-template-columns: 370px minmax(0, 1fr);
  gap: 18px;

  @media (max-width: 1120px) {
    grid-template-columns: 1fr;
  }
`;

const SideStack = styled.div`
  display: grid;
  gap: 18px;
  align-self: start;
`;

const RightStack = styled.div`
  display: grid;
  gap: 18px;
`;

const Panel = styled.section`
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 14, 26, 0.92);
  overflow: hidden;
  min-width: 0;
`;

const Toolbar = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const PanelBody = styled.div`
  padding: 18px;
`;

const PanelTitle = styled.div`
  color: #f8fafc;
  font-size: 1rem;
  font-weight: 900;
`;

const PanelSub = styled.p`
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.7;
`;

const FormGrid = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 12px;
`;

const Input = styled.input`
  width: 100%;
  min-height: 42px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  color: #f8fafc;
  padding: 0 14px;
  outline: none;

  &::placeholder {
    color: #64748b;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: ${({ $small }) => ($small ? "110px" : "320px")};
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  color: #dbeafe;
  padding: 14px;
  outline: none;
  resize: vertical;
  line-height: 1.75;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;

  &::placeholder {
    color: #64748b;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 12px;
  border: ${({ $primary }) => ($primary ? "none" : "1px solid rgba(148, 163, 184, 0.14)")};
  background: ${({ $primary }) =>
    $primary
      ? "linear-gradient(135deg, #f59e0b, #38bdf8)"
      : "rgba(15, 23, 42, 0.56)"};
  color: ${({ $primary }) => ($primary ? "#04111f" : "#f8fafc")};
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const OperationList = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 10px;
`;

const OperationButton = styled.button`
  padding: 14px;
  border-radius: 18px;
  border: 1px solid
    ${({ $active }) => ($active ? "rgba(56, 189, 248, 0.34)" : "rgba(148, 163, 184, 0.12)")};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(56, 189, 248, 0.12), rgba(245, 158, 11, 0.1))"
      : "rgba(15, 23, 42, 0.46)"};
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(56, 189, 248, 0.24);
  }
`;

const OperationTitle = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

const OperationText = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.65;
`;

const ClickableMeta = styled.div`
  color: ${({ $active }) => ($active ? "#7dd3fc" : "#64748b")};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-top: 10px;
`;

const TableList = styled.div`
  display: grid;
  gap: 14px;
`;

const TableCard = styled.div`
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(15, 23, 42, 0.42);
  overflow: hidden;
`;

const TableHead = styled.div`
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
`;

const TableName = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

const TableMeta = styled.div`
  color: #94a3b8;
  font-size: 12px;
`;

const TableWrap = styled.div`
  overflow: auto;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 420px;

  th,
  td {
    border-bottom: 1px solid rgba(148, 163, 184, 0.12);
    padding: 10px 12px;
    text-align: left;
    font-size: 13px;
  }

  th {
    color: #f8fafc;
    font-weight: 900;
    background: rgba(15, 23, 42, 0.48);
  }

  td {
    color: #cbd5e1;
  }
`;

const Empty = styled.div`
  padding: 18px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.7;
`;

const Status = styled.div`
  padding: 14px 16px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  color: ${({ $error }) => ($error ? "#fecaca" : "#94a3b8")};
  background: ${({ $error }) =>
    $error ? "rgba(127, 29, 29, 0.24)" : "rgba(15, 23, 42, 0.32)"};
  font-size: 13px;
  line-height: 1.7;
`;

const OPERATIONS = [
  {
    title: "Create Database",
    description: "Set a new database name and start fresh.",
    template: "CREATE DATABASE learning_lab;",
  },
  {
    title: "Create Table",
    description: "Create a table with columns you define.",
    template: "CREATE TABLE learners (id, name, track);",
  },
  {
    title: "Insert Row",
    description: "Insert row values into a table.",
    template: "INSERT INTO learners (id, name, track) VALUES (1, 'Aman', 'frontend');",
  },
  {
    title: "Select All",
    description: "View all rows from a table.",
    template: "SELECT * FROM learners;",
  },
  {
    title: "Select With Filter",
    description: "Filter rows with a WHERE condition.",
    template: "SELECT id, name FROM learners WHERE track = 'frontend';",
  },
  {
    title: "Update Rows",
    description: "Change values for rows matching a condition.",
    template: "UPDATE learners SET track = 'backend' WHERE name = 'Aman';",
  },
  {
    title: "Delete Rows",
    description: "Delete rows by condition.",
    template: "DELETE FROM learners WHERE id = 1;",
  },
  {
    title: "Drop Table",
    description: "Remove a table from the current database.",
    template: "DROP TABLE learners;",
  },
];

const initialDatabase = {
  name: "",
  tables: {},
};

const normalizeIdentifier = (value) => value.trim().replace(/;$/, "");

const parseLiteral = (value) => {
  const raw = value.trim();
  if ((raw.startsWith("'") && raw.endsWith("'")) || (raw.startsWith('"') && raw.endsWith('"'))) {
    return raw.slice(1, -1);
  }
  if (/^-?\d+(\.\d+)?$/.test(raw)) return Number(raw);
  if (/^(true|false)$/i.test(raw)) return raw.toLowerCase() === "true";
  if (/^null$/i.test(raw)) return null;
  return raw;
};

const splitCSV = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const cloneDb = (db) => ({
  name: db.name,
  tables: Object.fromEntries(
    Object.entries(db.tables).map(([key, table]) => [
      key,
      { columns: [...table.columns], rows: table.rows.map((row) => ({ ...row })) },
    ])
  ),
});

const rowsToResult = (rows, columns) =>
  rows.map((row) => {
    if (!columns || columns[0] === "*") return row;
    return Object.fromEntries(columns.map((column) => [column, row[column] ?? null]));
  });

const parseWhere = (clause) => {
  if (!clause) return null;
  const match = clause.match(/^\s*([a-zA-Z_][\w]*)\s*=\s*(.+)\s*$/i);
  if (!match) return null;
  return { column: match[1], value: parseLiteral(match[2]) };
};

function executeQuery(rawQuery, currentDb) {
  const query = rawQuery.trim().replace(/;$/, "");
  const db = cloneDb(currentDb);

  if (!query) {
    return { db, rows: [], message: "Write a query first." };
  }

  let match = query.match(/^CREATE\s+DATABASE\s+([a-zA-Z_][\w]*)$/i);
  if (match) {
    return {
      db: { name: match[1], tables: {} },
      rows: [],
      message: `Database '${match[1]}' created.`,
    };
  }

  match = query.match(/^CREATE\s+TABLE\s+([a-zA-Z_][\w]*)\s*\((.+)\)$/i);
  if (match) {
    const tableName = normalizeIdentifier(match[1]);
    const columns = splitCSV(match[2]).map((column) => column.replace(/\s+/g, ""));
    if (!columns.length) throw new Error("Add at least one column.");
    db.tables[tableName] = { columns, rows: [] };
    return { db, rows: [], message: `Table '${tableName}' created.` };
  }

  match = query.match(/^DROP\s+TABLE\s+([a-zA-Z_][\w]*)$/i);
  if (match) {
    const tableName = normalizeIdentifier(match[1]);
    if (!db.tables[tableName]) throw new Error(`Table '${tableName}' does not exist.`);
    delete db.tables[tableName];
    return { db, rows: [], message: `Table '${tableName}' dropped.` };
  }

  match = query.match(
    /^INSERT\s+INTO\s+([a-zA-Z_][\w]*)\s*(?:\((.+)\))?\s+VALUES\s*\((.+)\)$/i
  );
  if (match) {
    const tableName = normalizeIdentifier(match[1]);
    const table = db.tables[tableName];
    if (!table) throw new Error(`Table '${tableName}' does not exist.`);

    const columns = match[2] ? splitCSV(match[2]) : table.columns;
    const values = splitCSV(match[3]).map(parseLiteral);
    if (columns.length !== values.length) {
      throw new Error("Columns and values count must match.");
    }

    const row = Object.fromEntries(table.columns.map((column) => [column, null]));
    columns.forEach((column, index) => {
      row[column] = values[index];
    });
    table.rows.push(row);
    return { db, rows: table.rows, message: `1 row inserted into '${tableName}'.` };
  }

  match = query.match(
    /^SELECT\s+(.+)\s+FROM\s+([a-zA-Z_][\w]*)(?:\s+WHERE\s+(.+?))?(?:\s+ORDER\s+BY\s+([a-zA-Z_][\w]*)(?:\s+(ASC|DESC))?)?(?:\s+LIMIT\s+(\d+))?$/i
  );
  if (match) {
    const selectedColumns = splitCSV(match[1]);
    const tableName = normalizeIdentifier(match[2]);
    const whereClause = parseWhere(match[3]);
    const orderColumn = match[4];
    const orderDirection = (match[5] || "ASC").toUpperCase();
    const limit = match[6] ? Number(match[6]) : null;
    const table = db.tables[tableName];
    if (!table) throw new Error(`Table '${tableName}' does not exist.`);

    let rows = [...table.rows];
    if (whereClause) {
      rows = rows.filter((row) => String(row[whereClause.column]) === String(whereClause.value));
    }
    if (orderColumn) {
      rows.sort((a, b) => {
        const left = a[orderColumn];
        const right = b[orderColumn];
        if (left === right) return 0;
        if (orderDirection === "DESC") return left > right ? -1 : 1;
        return left > right ? 1 : -1;
      });
    }
    if (limit !== null) {
      rows = rows.slice(0, limit);
    }

    return {
      db,
      rows: rowsToResult(rows, selectedColumns),
      message: `${rows.length} row(s) selected from '${tableName}'.`,
    };
  }

  match = query.match(/^UPDATE\s+([a-zA-Z_][\w]*)\s+SET\s+(.+?)(?:\s+WHERE\s+(.+))?$/i);
  if (match) {
    const tableName = normalizeIdentifier(match[1]);
    const assignmentsRaw = splitCSV(match[2]);
    const whereClause = parseWhere(match[3]);
    const table = db.tables[tableName];
    if (!table) throw new Error(`Table '${tableName}' does not exist.`);

    const assignments = assignmentsRaw.map((assignment) => {
      const parts = assignment.split("=");
      if (parts.length !== 2) throw new Error("Invalid SET assignment.");
      return { column: parts[0].trim(), value: parseLiteral(parts[1]) };
    });

    let updated = 0;
    table.rows = table.rows.map((row) => {
      if (whereClause && String(row[whereClause.column]) !== String(whereClause.value)) {
        return row;
      }
      updated += 1;
      const next = { ...row };
      assignments.forEach(({ column, value }) => {
        next[column] = value;
      });
      return next;
    });

    return { db, rows: table.rows, message: `${updated} row(s) updated in '${tableName}'.` };
  }

  match = query.match(/^DELETE\s+FROM\s+([a-zA-Z_][\w]*)(?:\s+WHERE\s+(.+))?$/i);
  if (match) {
    const tableName = normalizeIdentifier(match[1]);
    const whereClause = parseWhere(match[2]);
    const table = db.tables[tableName];
    if (!table) throw new Error(`Table '${tableName}' does not exist.`);

    const previousCount = table.rows.length;
    table.rows = whereClause
      ? table.rows.filter(
          (row) => String(row[whereClause.column]) !== String(whereClause.value)
        )
      : [];
    return {
      db,
      rows: table.rows,
      message: `${previousCount - table.rows.length} row(s) deleted from '${tableName}'.`,
    };
  }

  throw new Error(
    "Supported queries: CREATE DATABASE, CREATE TABLE, INSERT INTO, SELECT, UPDATE, DELETE, DROP TABLE."
  );
}

function DataTableView({ rows, columns }) {
  if (!rows.length) {
    return <Empty>No rows to display.</Empty>;
  }

  const safeColumns = columns?.length ? columns : Object.keys(rows[0] || {});
  return (
    <TableWrap>
      <DataTable>
        <thead>
          <tr>
            {safeColumns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {safeColumns.map((column) => (
                <td key={column}>{String(row[column] ?? "null")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </DataTable>
    </TableWrap>
  );
}

export default function SqlPracticePage() {
  const editorPanelRef = useRef(null);
  const [database, setDatabase] = useState(initialDatabase);
  const [query, setQuery] = useState("");
  const [resultRows, setResultRows] = useState([]);
  const [status, setStatus] = useState(
    "Start by creating your own database and table, then add rows and run queries."
  );
  const [hasError, setHasError] = useState(false);
  const [newDbName, setNewDbName] = useState("learning_lab");
  const [newTableName, setNewTableName] = useState("");
  const [newColumns, setNewColumns] = useState("id, name, role");
  const tableNames = useMemo(() => Object.keys(database.tables), [database]);
  const [selectedTable, setSelectedTable] = useState("");
  const [rowJson, setRowJson] = useState('{"id": 3, "name": "Kabir", "track": "data"}');
  const [selectedOperationTitle, setSelectedOperationTitle] = useState("Select All");

  const handleCreateDatabase = () => {
    const name = newDbName.trim();
    if (!name) {
      setStatus("Add a database name first.");
      setHasError(true);
      return;
    }
    setDatabase({ name, tables: {} });
    setSelectedTable("");
    setResultRows([]);
    setStatus(`Database '${name}' created. Add your first table now.`);
    setHasError(false);
  };

  const handleCreateTable = () => {
    const tableName = newTableName.trim();
    const columns = splitCSV(newColumns);
    if (!tableName || !columns.length) {
      setStatus("Add a table name and at least one column.");
      setHasError(true);
      return;
    }

    setDatabase((prev) => ({
      ...prev,
      tables: {
        ...prev.tables,
        [tableName]: { columns, rows: [] },
      },
    }));
    setSelectedTable(tableName);
    setStatus(`Table '${tableName}' created.`);
    setHasError(false);
  };

  const handleAddRow = () => {
    if (!selectedTable) {
      setStatus("Select a table first.");
      setHasError(true);
      return;
    }

    try {
      const parsed = JSON.parse(rowJson);
      setDatabase((prev) => {
        const table = prev.tables[selectedTable];
        if (!table) return prev;
        return {
          ...prev,
          tables: {
            ...prev.tables,
            [selectedTable]: {
              ...table,
              rows: [...table.rows, parsed],
            },
          },
        };
      });
      setStatus(`Row inserted into '${selectedTable}'.`);
      setHasError(false);
    } catch (error) {
      setStatus(`Row JSON invalid: ${error.message}`);
      setHasError(true);
    }
  };

  const handleRunQuery = () => {
    try {
      const result = executeQuery(query, database);
      setDatabase(result.db);
      setResultRows(result.rows);
      setSelectedTable(Object.keys(result.db.tables)[0] || "");
      setStatus(result.message);
      setHasError(false);
    } catch (error) {
      setStatus(error.message);
      setHasError(true);
    }
  };

  const handleReset = () => {
    setDatabase(initialDatabase);
    setQuery("");
    setResultRows([]);
    setSelectedTable("");
    setStatus("Playground reset. Create a new database and table to begin again.");
    setHasError(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(query);
      setStatus("Query copied to clipboard.");
      setHasError(false);
    } catch {
      setStatus("Could not copy query from this browser session.");
      setHasError(true);
    }
  };

  const currentTable = selectedTable ? database.tables[selectedTable] : null;

  return (
    <Page>
      <Shell>
        <Hero>
          <Eyebrow>
            <Database size={14} />
            SQL Practice
          </Eyebrow>
          <Title>Build your own database playground and practice SQL operations</Title>
          <Sub>
            Create your own database, define tables, insert rows, and run supported SQL
            queries directly here. Learners can also click operation templates to understand
            how common SQL commands are written and used.
          </Sub>
        </Hero>

        <MainGrid>
          <SideStack>
            <Panel>
              <Toolbar>
                <PanelTitle>Database Setup</PanelTitle>
              </Toolbar>
              <PanelBody>
                <PanelSub>
                  Current database: <strong>{database.name || "Not created yet"}</strong>
                </PanelSub>
                <FormGrid>
                  <Input
                    value={newDbName}
                    onChange={(event) => setNewDbName(event.target.value)}
                    placeholder="New database name"
                  />
                  <Button $primary onClick={handleCreateDatabase}>
                    <Database size={15} />
                    Create Database
                  </Button>
                </FormGrid>

                <FormGrid style={{ marginTop: 18 }}>
                  <Input
                    value={newTableName}
                    onChange={(event) => setNewTableName(event.target.value)}
                    placeholder="Table name"
                  />
                  <Input
                    value={newColumns}
                    onChange={(event) => setNewColumns(event.target.value)}
                    placeholder="Columns: id, name, role"
                  />
                  <Button onClick={handleCreateTable}>
                    <Plus size={15} />
                    Create Table
                  </Button>
                </FormGrid>
              </PanelBody>
            </Panel>

            <Panel>
              <Toolbar>
                <PanelTitle>Row Inserter</PanelTitle>
              </Toolbar>
              <PanelBody>
                <PanelSub>
                  Select a table and add a JSON object row manually.
                </PanelSub>
                <FormGrid>
                  <Input
                    value={selectedTable}
                    onChange={(event) => setSelectedTable(event.target.value)}
                    placeholder="Target table"
                    list="table-options"
                  />
                  <datalist id="table-options">
                    {tableNames.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                  <Textarea
                    $small
                    value={rowJson}
                    onChange={(event) => setRowJson(event.target.value)}
                    placeholder='{"id": 1, "name": "Aman"}'
                  />
                  <Button onClick={handleAddRow}>
                    <Plus size={15} />
                    Add Row
                  </Button>
                </FormGrid>
              </PanelBody>
            </Panel>

            <Panel>
              <Toolbar>
                <PanelTitle>Learn SQL Operations</PanelTitle>
              </Toolbar>
              <PanelBody>
                <PanelSub>
                  Click any operation below to load its example query into the editor.
                </PanelSub>
                <OperationList>
                  {OPERATIONS.map((operation) => (
                    <OperationButton
                      key={operation.title}
                      $active={selectedOperationTitle === operation.title}
                      onClick={() => {
                        setSelectedOperationTitle(operation.title);
                        setQuery(operation.template);
                        editorPanelRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }}
                    >
                      <OperationTitle>{operation.title}</OperationTitle>
                      <OperationText>{operation.description}</OperationText>
                      <ClickableMeta $active={selectedOperationTitle === operation.title}>
                        {selectedOperationTitle === operation.title ? "Selected" : "Click to load"}
                      </ClickableMeta>
                    </OperationButton>
                  ))}
                </OperationList>
              </PanelBody>
            </Panel>
          </SideStack>

          <RightStack>
            <Panel>
              <div ref={editorPanelRef} />
              <Toolbar>
                <PanelTitle>SQL Query Editor</PanelTitle>
                <Row>
                  <Button onClick={handleCopy}>
                    <Copy size={15} />
                    Copy
                  </Button>
                  <Button onClick={handleReset}>
                    <RefreshCcw size={15} />
                    Reset
                  </Button>
                  <Button $primary onClick={handleRunQuery}>
                    <Play size={15} />
                    Run Query
                  </Button>
                </Row>
              </Toolbar>
              <PanelBody>
                <Textarea
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Write your SQL query here"
                />
              </PanelBody>
              <Status $error={hasError}>
                Supported commands: CREATE DATABASE, CREATE TABLE, INSERT INTO, SELECT, UPDATE, DELETE, DROP TABLE.
                <br />
                {status}
              </Status>
            </Panel>

            <Panel>
              <Toolbar>
                <PanelTitle>Current Tables & Data</PanelTitle>
              </Toolbar>
              <PanelBody>
                {tableNames.length ? (
                  <TableList>
                    {tableNames.map((tableName) => {
                      const table = database.tables[tableName];
                      return (
                        <TableCard key={tableName}>
                          <TableHead>
                            <div>
                              <TableName>{tableName}</TableName>
                              <TableMeta>{table.columns.join(", ")}</TableMeta>
                            </div>
                            <TableMeta>{table.rows.length} row(s)</TableMeta>
                          </TableHead>
                          <DataTableView rows={table.rows} columns={table.columns} />
                        </TableCard>
                      );
                    })}
                  </TableList>
                ) : (
                  <Empty>No tables yet. Create a database and table from the left panel.</Empty>
                )}
              </PanelBody>
            </Panel>

            <Panel>
              <Toolbar>
                <PanelTitle>Query Result</PanelTitle>
              </Toolbar>
              <DataTableView
                rows={resultRows}
                columns={resultRows.length ? Object.keys(resultRows[0]) : currentTable?.columns || []}
              />
              <Status>
                <Sparkles size={14} style={{ marginRight: 8, verticalAlign: "text-bottom" }} />
                This playground is built for learning and daily SQL practice, not as a full production SQL engine.
              </Status>
            </Panel>
          </RightStack>
        </MainGrid>
      </Shell>
    </Page>
  );
}
